// Firebase client initialization. Reads config from Vite env vars.
//
// In DEMO mode (no VITE_FIREBASE_API_KEY) we deliberately DON'T initialize the
// SDK — calling getAuth()/getFirestore() with an empty config throws
// `auth/invalid-api-key` at import time. Every backend call site is guarded by
// `isBackendConfigured`, so these exports are only ever used when configured.
import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getFirestore,
  connectFirestoreEmulator,
  type Firestore,
} from "firebase/firestore";
import { getAuth, connectAuthEmulator, type Auth } from "firebase/auth";
import {
  getFunctions,
  connectFunctionsEmulator,
  type Functions,
} from "firebase/functions";
import {
  getStorage,
  connectStorageEmulator,
  type FirebaseStorage,
} from "firebase/storage";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const configured = Boolean(firebaseConfig.apiKey);
const region = import.meta.env.VITE_FUNCTIONS_REGION || "me-central1";

// Only initialize when a real backend is configured. The casts keep the concrete
// types for consumers; in demo mode these are never dereferenced.
export const app = (configured ? initializeApp(firebaseConfig) : undefined) as FirebaseApp;
export const db = (configured ? getFirestore(app) : undefined) as Firestore;
export const auth = (configured ? getAuth(app) : undefined) as Auth;
export const functions = (configured ? getFunctions(app, region) : undefined) as Functions;
export const storage = (configured ? getStorage(app) : undefined) as FirebaseStorage;

// App Check (reCAPTCHA v3) — proves requests come from the real app. REQUIRED once
// the callable functions set enforceAppCheck:true, or their calls will be rejected.
if (configured && import.meta.env.VITE_RECAPTCHA_SITE_KEY) {
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(import.meta.env.VITE_RECAPTCHA_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
}

// Point at the local Emulator Suite during development.
if (configured && import.meta.env.VITE_USE_EMULATORS === "true") {
  connectFirestoreEmulator(db, "127.0.0.1", 8080);
  connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
  connectFunctionsEmulator(functions, "127.0.0.1", 5001);
  connectStorageEmulator(storage, "127.0.0.1", 9199);
}
